import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useFieldArray, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useAuth } from "hooks/useAuth";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";

const INITIAL_STATE = {
  name: "",
  location: { lat: "", lon: "" },
  contact_details: [{ name: "", email: "", phoneNumber: "" }],
  capacity: "",
  operational_status: true,
  account: "",
  entity: "",
  shipmentId: [],
  warehousingId: "",
  hasBranch: false,
  branch: null,
};

const WarehouseForm = ({
  isBusy = false,
  warehouseDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
      locationName: {
        locality: warehouseDetail?.location?.name || "",
        city: warehouseDetail?.location?.city || "",
      },
      hasBranch: !!warehouseDetail?.branch,
      branch: warehouseDetail?.branch || null,
      contact_details: warehouseDetail?.contact_details?.length
        ? warehouseDetail.contact_details.map((contact) => ({
            ...contact,
            phoneNumber:
              contact.phoneNumber ||
              `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
          }))
        : [
            {
              name: "",
              email: "",
              phoneNumber: `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
            },
          ],
    },
  });

  const { fields } = useFieldArray({ control, name: "contact_details" });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchHasBranch = useWatch({ control, name: "hasBranch" });

  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      location: {
        lat: parseFloat(data.location.lat),
        lon: parseFloat(data.location.lon),
        name: data.locationName?.locality || "Unknown Location",
        city: data.locationName?.city || "Unknown City",
      },
      account: data?.account?._id,
      entity: data?.entity?._id,
      branch: _watchHasBranch ? data?.branch?._id : null,
      contact_details: data.contact_details.map((contact) => ({
        ...contact,
        phoneNumber:
          contact.phoneNumber ||
          `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
      })),
    };
    delete formattedData.hasBranch;
    delete formattedData.locationName; // Remove frontend-only field
    onSubmit(formattedData);
  };

  React.useEffect(() => {
    if (!warehouseDetail) return;
    reset({
      ...INITIAL_STATE,
      ...warehouseDetail,
      locationName: {
        locality: warehouseDetail?.location?.name || "",
        city: warehouseDetail?.location?.city || "",
      },
      hasBranch: !!warehouseDetail?.branch,
      branch: warehouseDetail?.branch || null,
      contact_details: warehouseDetail?.contact_details?.length
        ? warehouseDetail.contact_details.map((contact) => ({
            ...contact,
            phoneNumber:
              contact.phoneNumber ||
              `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
          }))
        : [
            {
              name: "",
              email: "",
              phoneNumber: `${viewEntity?.entity?.country?.idd}--${viewEntity?.entity?.country?.cca2}`,
            },
          ],
    });
  }, [
    warehouseDetail,
    reset,
    viewEntity?.entity?.country?.idd,
    viewEntity?.entity?.country?.cca2,
  ]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [
    isAccountAdmin,
    isSystemAdmin,
    _watchAccount?._id,
    fetchEntities,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchBranches({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        active: "true",
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", null);
                      setValue("branch", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("branch", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="hasBranch"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          setValue("branch", null);
                        }
                      }}
                    />
                  }
                  label="Has Branch?"
                />
              )}
            />
          </Grid>

          {_watchHasBranch && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="branch"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Branch"
                    value={field.value || null}
                    options={branches || []}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(option) =>
                      option ? `${option.name} - ${option.code}` : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    helperText={errors?.branch?.message || "Optional"}
                    error={!!errors?.branch}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide a Warehouse Name." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Warehouse Name"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          {fields.map((item, index) => (
            <React.Fragment key={item.id}>
              <Grid item xs={6}>
                <Controller
                  name={`contact_details.${index}.name`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Contact Name"
                      error={!!errors.contact_details?.[index]?.name}
                      helperText={
                        errors.contact_details?.[index]?.name?.message
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name={`contact_details.${index}.email`}
                  control={control}
                  rules={{
                    pattern: {
                      value:
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                      message: "Invalid email format",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Email"
                      error={!!errors.contact_details?.[index]?.email}
                      helperText={
                        errors.contact_details?.[index]?.email?.message
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name={`contact_details.${index}.phoneNumber`}
                  control={control}
                  rules={{
                    required: "Phone number is required",

                    validate: (value) => {
                      const digits = value.replace(/\D/g, "");
                      if (digits.length < 7 || digits.length > 17) {
                        return "Phone number must be between 9 and 16 digits";
                      }
                      return true;
                    },
                    // validate: (value) => {
                    //   const expectedIdd = viewEntity?.entity?.country?.idd; // e.g. '977'
                    //   const expectedCca2 = viewEntity?.entity?.country?.cca2; // e.g. 'NP'

                    //   if (!expectedIdd || !expectedCca2)
                    //     return "Country code information missing";

                    //   const pattern = new RegExp(
                    //     `^${expectedIdd}-(\\d{7}|\\d{10})-${expectedCca2}$`
                    //   );
                    //   if (!pattern.test(value)) {
                    //     return `Phone number must be in format: ${expectedIdd}-<7 or 10 digit number>-${expectedCca2}`;
                    //   }

                    //   return true;
                    // },
                  }}
                  render={({ field }) => (
                    <CustomCountryPhoneInput
                      placeholder="Phone Number"
                      {...field}
                      lockedCountry={viewEntity?.entity?.country}
                      error={!!errors.contact_details?.[index]?.phoneNumber}
                      helperText={
                        errors.contact_details?.[index]?.phoneNumber?.message
                      }
                    />
                  )}
                />
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={6}>
            <Controller
              name="location.lat"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Latitude"
                  type="number"
                  error={!!errors.location?.lat}
                  helperText={errors.location?.lat?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="location.lon"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Longitude"
                  type="number"
                  error={!!errors.location?.lon}
                  helperText={errors.location?.lon?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="capacity"
              control={control}
              // rules={{
              //   required: "Please provide a Capacity value.",
              //   min: { value: 1, message: "Capacity must be at least 1." },
              // }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Capacity"
                  type="number"
                  error={!!errors.capacity}
                  helperText={errors?.capacity?.message}
                />
              )}
            />
          </Grid>

          {warehouseDetail && (
            <Grid item xs={6}>
              <Controller
                name="operational_status"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Operational Status" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {warehouseDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default WarehouseForm;